import { useCallback, useMemo } from 'react'

import { useRouter } from 'next/router'

import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import {
  WebCheckoutTrackPage,
  WebCheckoutTrackSystem,
  WebCheckoutTrackUser,
  WebCheckoutTrackView,
} from '@squareup/customer-data-events-web'

import { useAuth } from '../components/AuthProvider'
import {
  PageNames,
  Route,
  TrackingErrorCodeEventWhitelist,
  TrackingSystemActions,
  TrackingUserActions,
  TrackingViews,
} from '../model'
import { useCheckoutRequestId, useError, useMerchant, useOrderId, usePaymentMode, useSessionRequestId } from '../state'
import { getPlatform, useLoanDetails, useOrderTotal } from '../utils'
import { getAppConfig } from './config'

export const viewedPageEventName = (pageName: string) => `Viewed ${pageName} Page`

export const useRoutePageName = () => {
  const { pathname } = useRouter()

  return PageNames[pathname as Route]
}

const cleanValue = <T>(val: T) => (typeof val === 'undefined' || val === '' ? undefined : val)

export const useEnduringProperties = () => {
  const { appVersion } = getAppConfig()
  const customerRequestId = useCheckoutRequestId()
  const sessionRequestId = useSessionRequestId()
  const merchant = useMerchant()
  const orderId = useOrderId()
  const orderTotal = useOrderTotal()
  const platform = getPlatform()

  return useMemo(
    () => ({
      customer_request_id: cleanValue(customerRequestId),
      session_request_id: cleanValue(sessionRequestId),
      merchant_name: cleanValue(merchant.name),
      order_id: cleanValue(orderId),
      order_amount_cents: cleanValue(orderTotal?.amount),
      order_currency: cleanValue(orderTotal?.currency),
      platform,
      version: appVersion,
    }),
    [
      appVersion,
      customerRequestId,
      merchant.name,
      orderTotal?.amount,
      orderTotal?.currency,
      orderId,
      platform,
      sessionRequestId,
    ]
  )
}

export const useTrackUserEvent = () => {
  const { analyticsHost } = getAppConfig()
  const logCdfEvent = useLogCashWebCdfEvent({ hostName: analyticsHost })

  const enduringProperties = useEnduringProperties()
  const payment_mode = usePaymentMode()
  const loanDetails = useLoanDetails()

  return useCallback(
    (eventName: TrackingUserActions) => {
      const detail = JSON.stringify({
        ...enduringProperties,
        payment_mode,
        ...loanDetails,
      })

      logCdfEvent(
        WebCheckoutTrackUser({
          action: eventName,
          customerRequestId: enduringProperties.customer_request_id,
          sessionRequestId: enduringProperties.session_request_id,
          detail,
        })
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payment_mode]
  )
}

export const useTrackSystemEvent = () => {
  const { analyticsHost } = getAppConfig()
  const logCdfEvent = useLogCashWebCdfEvent({ hostName: analyticsHost })

  const enduringProperties = useEnduringProperties()

  return useCallback(
    (eventName: TrackingSystemActions) => {
      const detail = JSON.stringify({
        ...enduringProperties,
      })

      logCdfEvent(
        WebCheckoutTrackSystem({
          action: eventName,
          customerRequestId: enduringProperties.customer_request_id,
          sessionRequestId: enduringProperties.session_request_id,
          detail,
        })
      )
    },
    [enduringProperties, logCdfEvent]
  )
}

interface ErrorDetails {
  error_code?: string
  error_category?: string
}

export const useTrackPageView = () => {
  const { analyticsHost } = getAppConfig()
  const logCdfEvent = useLogCashWebCdfEvent({ hostName: analyticsHost })

  const enduringProperties = useEnduringProperties()
  const loanDetails = useLoanDetails()
  const error = useError()

  return useCallback(
    (pageName: string) => {
      const errorDetails: ErrorDetails = {}
      const isErrorEvent = TrackingErrorCodeEventWhitelist.includes(pageName)
      if (isErrorEvent) {
        errorDetails.error_code = error.code
        errorDetails.error_category = error.category
      }
      const detail = JSON.stringify({
        ...enduringProperties,
        ...loanDetails,
        ...errorDetails,
      })

      logCdfEvent(
        WebCheckoutTrackPage({
          action: viewedPageEventName(pageName),
          customerRequestId: enduringProperties.customer_request_id,
          sessionRequestId: enduringProperties.session_request_id,
          detail,
        })
      )
    },
    [enduringProperties, error.category, error.code, loanDetails, logCdfEvent]
  )
}

export const useTrackView = () => {
  const { loggedIn } = useAuth()
  const { analyticsHost } = getAppConfig()
  const logCdfEvent = useLogCashWebCdfEvent({ hostName: analyticsHost })

  const enduringProperties = useEnduringProperties()

  return useCallback(
    (eventName: TrackingViews, error_code?: string) => {
      if (!loggedIn) {
        return
      }
      const detail = JSON.stringify({
        ...enduringProperties,
        error_code,
      })

      logCdfEvent(
        WebCheckoutTrackView({
          action: eventName,
          customerRequestId: enduringProperties.customer_request_id,
          sessionRequestId: enduringProperties.session_request_id,
          detail,
        })
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loggedIn]
  )
}
